import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, Pencil, Check, X, Loader2, Tags } from "lucide-react";
import { toast } from "sonner";
import { fetchCategories, createCategory, updateCategory, deleteCategory } from "@/lib/api";

export default function AdminCategories() {
  const qc = useQueryClient();
  const [newName, setNewName] = useState("");
  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState("");

  const { data: categories = [], isLoading } = useQuery({ queryKey: ["categories"], queryFn: fetchCategories });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["categories"] }); };

  const addMut = useMutation({
    mutationFn: (name) => createCategory({ name, order: categories.length + 1 }),
    onSuccess: () => { refresh(); setNewName(""); toast.success("Kategori eklendi"); },
    onError: (e) => toast.error(e.response?.data?.detail || "Eklenemedi"),
  });
  const updMut = useMutation({
    mutationFn: ({ id, name, order }) => updateCategory(id, { name, order }),
    onSuccess: () => { refresh(); setEditing(null); toast.success("Güncellendi"); },
    onError: () => toast.error("Güncellenemedi"),
  });
  const delMut = useMutation({
    mutationFn: (id) => deleteCategory(id),
    onSuccess: () => { refresh(); toast.success("Kategori silindi"); },
    onError: (e) => toast.error(e.response?.data?.detail || "Silinemedi"),
  });

  return (
    <div className="mx-auto max-w-2xl">
      <h1 className="mb-1 text-2xl font-bold">Kategoriler</h1>
      <p className="mb-6 text-sm text-zinc-500">Ürünlerinizi gruplamak için kategorileri yönetin.</p>

      <form
        onSubmit={(e) => { e.preventDefault(); if (newName.trim()) addMut.mutate(newName.trim()); }}
        className="mb-6 flex gap-3"
      >
        <input
          data-testid="new-category-input"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="Yeni kategori adı (örn: Baret)"
          className="flex-1 rounded-lg border border-zinc-300 px-3.5 py-2.5 text-sm outline-none focus:border-primary"
        />
        <button data-testid="add-category-btn" type="submit" disabled={addMut.isPending}
          className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary/90 disabled:opacity-60">
          <Plus className="h-4 w-4" /> Ekle
        </button>
      </form>

      {isLoading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : (
        <div className="divide-y divide-zinc-100 overflow-hidden rounded-xl border border-zinc-200 bg-white">
          {categories.map((c) => (
            <div key={c.id} data-testid={`category-${c.slug}`} className="flex items-center justify-between px-4 py-3">
              {editing === c.id ? (
                <input autoFocus value={editName} onChange={(e) => setEditName(e.target.value)}
                  className="flex-1 rounded-md border border-zinc-300 px-2 py-1 text-sm outline-none focus:border-primary" />
              ) : (
                <div className="flex items-center gap-3">
                  <span className="flex h-8 w-8 items-center justify-center rounded-md bg-zinc-100"><Tags className="h-4 w-4 text-zinc-500" /></span>
                  <div>
                    <div className="font-medium text-zinc-900">{c.name}</div>
                    <div className="text-xs text-zinc-400">{c.slug}</div>
                  </div>
                </div>
              )}
              <div className="flex gap-2">
                {editing === c.id ? (
                  <>
                    <button onClick={() => updMut.mutate({ id: c.id, name: editName, order: c.order })}
                      className="flex h-8 w-8 items-center justify-center rounded-md border border-green-200 text-green-600 hover:bg-green-50"><Check className="h-4 w-4" /></button>
                    <button onClick={() => setEditing(null)}
                      className="flex h-8 w-8 items-center justify-center rounded-md border border-zinc-200 text-zinc-500 hover:bg-zinc-100"><X className="h-4 w-4" /></button>
                  </>
                ) : (
                  <>
                    <button data-testid={`edit-cat-${c.slug}`} onClick={() => { setEditing(c.id); setEditName(c.name); }}
                      className="flex h-8 w-8 items-center justify-center rounded-md border border-zinc-200 text-zinc-600 hover:bg-zinc-100"><Pencil className="h-4 w-4" /></button>
                    <button data-testid={`del-cat-${c.slug}`} onClick={() => delMut.mutate(c.id)}
                      className="flex h-8 w-8 items-center justify-center rounded-md border border-red-200 text-red-600 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
                  </>
                )}
              </div>
            </div>
          ))}
          {categories.length === 0 && <p className="py-10 text-center text-sm text-zinc-400">Henüz kategori yok.</p>}
        </div>
      )}
      <p className="mt-3 text-xs text-zinc-400">Not: İçinde ürün bulunan bir kategori silinemez.</p>
    </div>
  );
}
